// 261010 P8b-2 — khối cảnh: heading · summary · ảnh bối cảnh · N video · thời lượng. BE cũ (không scenes[]) ⇒ header cũ.
import React, { useState } from 'react';
import type { WorkflowSceneInfo } from '../../types/filmWorkflow';
import type { SceneGroup } from './workflowBoardUtils';
import { formatClock } from './workflowBoardUtils';
import { Tooltip } from 'antd';
import WfIcon from './WfIcon';
import { IconChip } from './StatusIcon';
import { sceneLineChipsOf } from './workflowStatusIcons';

const LineChips: React.FC<{ line?: string }> = ({ line }) => (
  <span className="wf-scene-chips">{sceneLineChipsOf(line).map((c, i) => <IconChip key={i} icon={c.icon} label={c.label} value={c.value} tone={c.tone ?? 'meta'} focusable={false} />)}</span>
);

interface Props {
  group: SceneGroup;
  info?: WorkflowSceneInfo;
  children: React.ReactNode;
  /** 261010 UI1a — màn bước: video xếp DỌC (hàng) thay vì lưới thẻ. */
  rows?: boolean;
  /** 261010 UI1c — cảnh thu/mở. `collapsible` ⇒ có nút; `open=false` ⇒ chỉ một dòng `line`. `locked` = không mở được (cảnh không có việc cần xử lý khi đang lọc "Cần xử lý"). */
  collapsible?: boolean;
  open?: boolean;
  line?: string;
  locked?: boolean;
  onToggle?: () => void;
  /** 261010 P9D D5 — công cụ cấp cảnh (giờ · khoá · nhân vật) ở bước 1. Vắng ⇒ DOM y nguyên. */
  tools?: React.ReactNode;
}

/** Khung giữ chỗ khi cảnh chưa có ảnh bối cảnh: biểu tượng ảnh + Tooltip. */
const NoImage: React.FC = () => (
  <Tooltip title="Chưa có ảnh bối cảnh"><span className="wf-slate-ph" role="img" aria-label="Chưa có ảnh bối cảnh"><WfIcon name="picture" /></span></Tooltip>
);

/** «N video · mm:ss» → biểu tượng video + số, đồng hồ + thời lượng. */
const SceneMeta: React.FC<{ count: number; dur: number | null | undefined }> = ({ count, dur }) => (
  <span className="wf-scene-meta">
    <IconChip icon="video" label={`${count} video`} value={String(count)} tone="meta" />
    {dur != null && Number.isFinite(dur) && dur > 0 ? <IconChip icon="clock" label={`Tổng thời lượng ${formatClock(dur)}`} value={formatClock(dur)} tone="meta" /> : null}
  </span>
);

const LocImg: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [hide, setHide] = useState(false);
  if (hide) return <NoImage />;
  return <img className="wf-slate-img" src={src} alt={alt} loading="lazy" decoding="async" onError={() => setHide(true)} />;
};

const WorkflowSceneBlock: React.FC<Props> = ({ group, info, children, rows, collapsible, open = true, line, locked, onToggle, tools }) => {
  const heading = info?.heading || group.heading;
  const count = info?.videoCount ?? group.events.length;
  const dur = info?.totalDurationS;
  if (collapsible && !open) {
    return (
      <section className="wf-scene wf-scene-closed" aria-label={`Cảnh ${group.sceneOrder}`} data-testid={`wf-scene-${group.sceneOrder}`}>
        <button type="button" className={`wf-scene-tg${locked ? ' locked' : ''}`} disabled={locked} aria-expanded={false} onClick={onToggle}>
          <span className="chev" aria-hidden="true">{locked ? null : <WfIcon name="right" />}</span><span className="tag">CẢNH {group.sceneOrder}</span>
          <span className="ln"><LineChips line={line} /></span><span className="hd" title={heading}>{heading}</span>
          {locked ? <IconChip icon="check" label="Không có việc cần xử lý" tone="meta" className="lock" focusable={false} /> : null}
        </button>
      </section>
    );
  }
  return (
    <section className="wf-scene" aria-label={`Cảnh ${group.sceneOrder}`} data-testid={`wf-scene-${group.sceneOrder}`}>
      {collapsible ? (
        <Tooltip title={`Thu cảnh ${group.sceneOrder}`}>
          <button type="button" className="wf-scene-fold" aria-expanded aria-label={`Thu cảnh ${group.sceneOrder}`} onClick={onToggle}>
            <WfIcon name="down" />{line ? <LineChips line={line} /> : null}
          </button>
        </Tooltip>) : null}
      {info ? (
        <div className="wf-slate">
          <span className="tag">CẢNH {group.sceneOrder}</span>
          <div className="wf-slate-t">
            <div className="t" title={heading}>{heading}</div>
            {info.summary ? <div className="wf-slate-s" title={info.summary}>{info.summary}</div> : null}
          </div>
          <div className="wf-slate-m">
            {info.imageUrl
              ? <LocImg src={info.imageUrl} alt={`Ảnh bối cảnh ${info.locationName || heading}`} />
              : <NoImage />}
            <SceneMeta count={count} dur={dur} />
          </div>
        </div>
      ) : (
        <div className="wf-scene-h"><span className="tag">CẢNH {group.sceneOrder}</span><span className="t" title={heading}>{heading}</span><span className="c"><SceneMeta count={count} dur={dur} /></span></div>
      )}
      {tools}
      <div className={rows ? 'wf-rows' : 'wf-grid'}>{children}</div>
    </section>
  );
};

export default WorkflowSceneBlock;
